import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { clearBrandingCache } from '../components/BrandingLoader';
import { clearAllCache } from '../utils/offlineDB';

const AuthContext = createContext(null);

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

// Auto-Logout nach Inaktivität (Sicherheits-Anforderung von H&D/Dreier 2026-05-13).
// 60 Min ohne Maus/Tastatur/Touch → automatischer Logout + Redirect zur Login-Seite.
// Reset über User-Events (mousemove, mousedown, keydown, touchstart, scroll).
const IDLE_TIMEOUT_MS = 60 * 60 * 1000;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchCurrentUser(token);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchCurrentUser = async (token, shouldRedirect = true) => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      const userData = response.data;
      setUser(userData);

      // Prüfen ob Onboarding abgeschlossen ist (nur für Organisation-Owner)
      // und nur wenn wir nicht bereits auf der Onboarding-Seite sind
      if (shouldRedirect && userData.isOrganisationOwner && userData.organisation && !userData.organisation.onboardingCompleted) {
        const currentPath = window.location.pathname;
        if (currentPath !== '/onboarding') {
          navigate('/onboarding');
        }
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      const status = error.response?.status;
      const errorCode = error.response?.data?.error;
      const isTokenError = status === 401 ||
        (status === 403 && errorCode?.includes('token'));

      // Portal-Isolation: User ist auf dem falschen Portal (z.B. STANDALONE auf kikichat.de)
      // → ausloggen und auf Login-Screen mit Fehlermeldung
      if (errorCode === 'portal_org_mismatch') {
        localStorage.removeItem('token');
        setUser(null);
        navigate('/login', { state: { error: error.response?.data?.message } });
        return;
      }

      // Nur bei echtem Auth-Fehler (ungültiges Token) ausloggen
      // Bei Netzwerkfehlern Token behalten
      if (isTokenError) {
        localStorage.removeItem('token');
      } else {
        // Bei Netzwerkfehler: User aus Token dekodieren (Fallback)
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          setUser({ id: payload.id, email: payload.email, name: payload.name, rolle: payload.rolle });
        } catch {
          // Token ungültig, löschen
          localStorage.removeItem('token');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, passwort) => {
    try {
      const response = await axios.post(`${API_URL}/auth/login`, {
        email,
        passwort
      });

      const { token, user } = response.data;
      // Alle Caches löschen BEVOR neuer Token gesetzt wird
      clearBrandingCache();
      await clearAllCache();
      localStorage.setItem('token', token);
      setUser(user);

      // Prüfen ob Trial abgelaufen / Organisation suspendiert
      if (user.organisation?.planStatus === 'SUSPENDED') {
        navigate('/einstellungen/abonnement');
      } else if (user.organisation && !user.organisation.onboardingCompleted) {
        navigate('/onboarding');
      } else {
        navigate('/dashboard');
      }
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);

      // Prüfen ob E-Mail-Verifizierung erforderlich ist
      if (error.response?.data?.needsVerification) {
        return {
          success: false,
          needsVerification: true,
          email: error.response.data.email
        };
      }

      // Bessere Fehlermeldungen für verschiedene Fehlertypen
      let errorMessage = 'Login fehlgeschlagen';

      if (error.response?.data?.error) {
        // Server hat geantwortet mit Fehler
        errorMessage = error.response.data.error;
      } else if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        // Netzwerkfehler
        errorMessage = 'Server nicht erreichbar. Bitte überprüfen Sie Ihre Internetverbindung oder versuchen Sie http://46.224.120.38:3000/health zu öffnen.';
      } else if (error.code === 'ERR_CERT_AUTHORITY_INVALID' || error.message?.includes('certificate')) {
        // SSL-Zertifikatsfehler
        errorMessage = 'SSL-Zertifikat nicht vertraut. Bitte http://46.224.120.38:3000/health im Browser öffnen.';
      } else if (!navigator.onLine) {
        // Wirklich offline
        errorMessage = 'Keine Internetverbindung';
      }

      return {
        success: false,
        error: errorMessage
      };
    }
  };

  const logout = useCallback(async (reason = null) => {
    localStorage.removeItem('token');
    localStorage.removeItem('chatLastKonversationId');
    // Alle Caches löschen beim Logout
    clearBrandingCache();
    await clearAllCache();
    setUser(null);
    if (reason === 'idle') {
      navigate('/login?reason=idle');
    } else {
      navigate('/login');
    }
  }, [navigate]);

  // Idle-Logout: nur aktiv solange ein User eingeloggt ist. Timer wird bei jedem
  // User-Event resettet, läuft sonst nach IDLE_TIMEOUT_MS aus → logout('idle').
  // Cross-Tab-Sync via storage-Event: wenn ein anderer Tab den Token entfernt
  // (Logout dort), reagiert dieser Tab ebenfalls.
  const idleTimerRef = useRef(null);
  useEffect(() => {
    if (!user) return;

    const reset = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => {
        logout('idle');
      }, IDLE_TIMEOUT_MS);
    };

    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach(ev => window.addEventListener(ev, reset, { passive: true }));

    const onStorage = (e) => {
      if (e.key === 'token' && !e.newValue) {
        if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
        setUser(null);
      }
    };
    window.addEventListener('storage', onStorage);

    reset();
    return () => {
      events.forEach(ev => window.removeEventListener(ev, reset));
      window.removeEventListener('storage', onStorage);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [user, logout]);

  const refreshUser = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      await fetchCurrentUser(token);
    }
  };

  const register = async (firmenname, name, email, password) => {
    try {
      const response = await axios.post(`${API_URL}/auth/register`, {
        firmenname,
        name,
        email,
        password
      });

      // Prüfen ob E-Mail-Verifizierung erforderlich ist
      if (response.data.needsVerification) {
        return {
          success: true,
          needsVerification: true,
          email: response.data.email
        };
      }

      // Falls Token direkt zurückgegeben wird (Fallback)
      const { token, user } = response.data;
      // Alle Caches löschen BEVOR neuer Token gesetzt wird
      clearBrandingCache();
      await clearAllCache();
      localStorage.setItem('token', token);
      setUser(user);

      // Nach Registrierung immer zum Onboarding
      navigate('/onboarding');
      return { success: true };
    } catch (error) {
      console.error('Register error:', error);

      let errorMessage = 'Registrierung fehlgeschlagen';

      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        errorMessage = 'Server nicht erreichbar. Bitte versuchen Sie es später erneut.';
      } else if (!navigator.onLine) {
        errorMessage = 'Keine Internetverbindung';
      }

      return {
        success: false,
        error: errorMessage
      };
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, register, refreshUser, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
